import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { AssistantContextService } from '../assistant-context.service';
import { AssistantService } from '../assistant.service';

/** durata dell'intro (etichetta aperta + brillio): allineata alle animazioni in scss */
const INTRO_MS = 4200;
/** rientrare nello stesso punto a breve non ripete l'intro: richiama l'attenzione, non la disturba */
const INTRO_COOLDOWN_MS = 30000;

/**
 * Linguetta flottante sul bordo destro a metà altezza: apre il pannello dell'assistente.
 * Icona auto_awesome in fontSet Round (anche in pannello e banner): lo stile pieno è il font npm del 2016 senza
 * le icone recenti, e general.scss forza Outlined solo su mat-icon.material-icons.
 * Nascosta a pannello aperto (si chiude dalla X). Visibile solo con modulo ai_assistant attivo sul dominio.
 * L'angolo in basso a destra resta al widget di assistenza Freshworks.
 * Intro all'ingresso in un punto abilitato (anchorAdded): etichetta + brillio una volta, a pannello chiuso.
 * Vibrazione leggera a intervalli (solo CSS) finché l'utente non apre l'assistente la prima volta nella sessione.
 */
@Component({
	selector: 'assistant-launcher',
	templateUrl: './assistant-launcher.component.html',
	styleUrls: ['./assistant-launcher.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AssistantLauncherComponent implements OnDestroy{

	intro = false;
	/** vibrazione periodica: si spegne alla prima apertura del pannello (l'assistente è stato notato) */
	nudge = true;

	private lastIntroAt = 0;
	private introTimer: Subscription | null = null;
	private readonly destroyed = new Subject<void>();

	constructor(
		public assistant: AssistantService,
		contextService: AssistantContextService,
		private changeDetector: ChangeDetectorRef
	){
		contextService.anchorAdded.pipe(
			debounceTime(300),
			// il pulsante deve esistere: modulo attivo e utente abilitato (available$ arriva dopo il context)
			switchMap(() => this.assistant.available$.pipe(filter(Boolean), take(1))),
			filter(() => !this.assistant.open.value && Date.now() - this.lastIntroAt > INTRO_COOLDOWN_MS),
			takeUntil(this.destroyed)
		).subscribe(() => this.playIntro());
		this.assistant.open.pipe(filter(Boolean), takeUntil(this.destroyed)).subscribe(() => {
			this.nudge = false;
			this.stopIntro();
		});
	}

	ngOnDestroy(): void{
		this.introTimer?.unsubscribe();
		this.destroyed.next();
		this.destroyed.complete();
	}

	private playIntro(): void{
		this.lastIntroAt = Date.now();
		this.intro = true;
		this.changeDetector.markForCheck();
		this.introTimer?.unsubscribe();
		this.introTimer = timer(INTRO_MS).subscribe(() => this.stopIntro());
	}

	private stopIntro(): void{
		if(!this.intro) return;
		this.intro = false;
		this.changeDetector.markForCheck();
	}
}
